import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CategoryService } from '@admin-core/services/category.service';
import { Category, CategoryTree } from '@admin-core/models/category.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { slugify } from '@admin-shared/utils/slug.util';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';
import { ImgFallbackDirective } from '@admin-shared/directives/img-fallback.directive';
import { of, switchMap } from 'rxjs';

interface FlatCategory extends Category {
  level: number;
  parentName: string | null;
  siblingIndex: number;
  siblingCount: number;
}

@Component({
  selector: 'app-categories-admin',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, TranslatePipe, ImgFallbackDirective],
  templateUrl: './categories-admin.component.html',
  styleUrl: './categories-admin.component.scss'
})
export class CategoriesAdminComponent implements OnInit {
  private readonly api = inject(CategoryService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly tree = signal<CategoryTree[]>([]);
  readonly loading = signal(false);
  readonly dialogOpen = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly dirtyParents = signal<Set<string | 'root'>>(new Set());
  readonly imagePreview = signal<string | null>(null);
  readonly pendingImageFile = signal<File | null>(null);
  readonly pendingRemoveImage = signal(false);

  readonly flat = computed(() => {
    const rows: FlatCategory[] = [];
    const roots = this.tree();
    roots.forEach((parent, rootIndex) => {
      rows.push({
        id: parent.id,
        name: parent.name,
        slug: parent.slug,
        parentId: null,
        displayOrder: parent.displayOrder,
        imageUrl: parent.imageUrl,
        isActive: parent.isActive,
        listsBundles: parent.listsBundles,
        level: 0,
        parentName: null,
        siblingIndex: rootIndex,
        siblingCount: roots.length
      });
      const children = parent.children ?? [];
      children.forEach((child, childIndex) => {
        rows.push({
          ...child,
          level: 1,
          parentName: parent.name,
          siblingIndex: childIndex,
          siblingCount: children.length
        });
      });
    });
    return rows;
  });

  readonly hasDirtyOrder = computed(() => this.dirtyParents().size > 0);

  readonly parentOptions = computed(() =>
    this.tree().map((c) => ({ id: c.id, name: c.name }))
  );

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    slug: ['', [Validators.required, Validators.maxLength(200)]],
    parentId: this.fb.control<string | null>(null),
    isActive: [true]
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getTree().subscribe({
      next: (tree) => {
        this.tree.set(tree);
        this.dirtyParents.set(new Set());
        this.loading.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('categories.loadError'));
        this.loading.set(false);
      }
    });
  }

  move(item: FlatCategory, direction: -1 | 1): void {
    const target = item.siblingIndex + direction;
    if (target < 0 || target >= item.siblingCount) {
      return;
    }

    const next = this.tree().map((parent) => ({
      ...parent,
      children: [...(parent.children ?? [])]
    }));

    if (item.level === 0) {
      const roots = next;
      [roots[item.siblingIndex], roots[target]] = [roots[target], roots[item.siblingIndex]];
      this.tree.set(roots);
      this.markDirty('root');
      return;
    }

    const parentIndex = next.findIndex((p) => p.id === item.parentId);
    if (parentIndex < 0) {
      return;
    }
    const children = next[parentIndex].children;
    [children[item.siblingIndex], children[target]] = [children[target], children[item.siblingIndex]];
    this.tree.set(next);
    this.markDirty(item.parentId!);
  }

  saveOrder(): void {
    const dirty = [...this.dirtyParents()];
    if (!dirty.length) {
      return;
    }

    const tree = this.tree();
    let pending = dirty.length;
    let failed = false;

    for (const key of dirty) {
      const parentId = key === 'root' ? null : key;
      const ids =
        key === 'root'
          ? tree.map((p) => p.id)
          : (tree.find((p) => p.id === key)?.children ?? []).map((c) => c.id);

      this.api.setOrder(parentId, ids).subscribe({
        next: () => {
          pending -= 1;
          if (pending === 0 && !failed) {
            this.load();
          }
        },
        error: (err) => {
          failed = true;
          this.showError(err?.error?.error ?? this.i18n.t('categories.orderError'));
        }
      });
    }
  }

  openCreate(parentId: string | null = null): void {
    this.editingId.set(null);
    this.form.reset({ name: '', slug: '', parentId, isActive: true });
    this.resetImageState();
    this.dialogOpen.set(true);
  }

  openEdit(item: FlatCategory): void {
    this.editingId.set(item.id);
    this.form.setValue({
      name: item.name,
      slug: item.slug,
      parentId: item.parentId,
      isActive: item.isActive
    });
    this.resetImageState(item.imageUrl ?? null);
    this.dialogOpen.set(true);
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.resetImageState();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) {
      return;
    }

    this.pendingImageFile.set(file);
    this.pendingRemoveImage.set(false);
    this.imagePreview.set(URL.createObjectURL(file));
  }

  clearImage(): void {
    this.pendingImageFile.set(null);
    this.pendingRemoveImage.set(true);
    this.imagePreview.set(null);
  }

  onNameChange(value: string): void {
    if (!this.editingId()) {
      this.form.controls.slug.setValue(slugify(value));
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const payload = {
      name: raw.name,
      slug: raw.slug,
      parentId: raw.parentId || null,
      isActive: raw.isActive
    };

    const id = this.editingId();
    const req$ = id ? this.api.update(id, payload) : this.api.create(payload);

    req$
      .pipe(
        switchMap((category) => {
          const file = this.pendingImageFile();
          if (file) {
            return this.api.uploadImage(category.id, file);
          }
          if (this.pendingRemoveImage() && this.editingId()) {
            return this.api.deleteImage(category.id);
          }
          return of(category);
        })
      )
      .subscribe({
        next: () => {
          this.closeDialog();
          this.load();
        },
        error: (err) =>
          this.showError(err?.error?.error ?? this.i18n.t('categories.saveError'))
      });
  }

  async remove(item: FlatCategory): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('categories.deleteConfirm', { name: item.name }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.api.delete(item.id).subscribe({
      next: () => this.load(),
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('categories.deleteError'))
    });
  }

  private resetImageState(preview: string | null = null): void {
    this.pendingImageFile.set(null);
    this.pendingRemoveImage.set(false);
    this.imagePreview.set(preview);
  }

  private markDirty(key: string | 'root'): void {
    const next = new Set(this.dirtyParents());
    next.add(key);
    this.dirtyParents.set(next);
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
